import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsPlusLg, BsTrash, BsPencil, BsCheckCircle, BsX } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";

const STATO_CFG = {
  bozza:        { cls: "bg-gray-100 text-gray-600" },
  in_revisione: { cls: "bg-yellow-100 text-yellow-700" },
  approvato:    { cls: "bg-green-100 text-green-700" },
};

const CATEGORIA_OPTIONS = [
  { value: "fattibilita_tecnica",         label: "Fattibilità tecnica" },
  { value: "fabbisogni_ente",             label: "Fabbisogni dell'ente" },
  { value: "piano_economico_finanziario", label: "Piano economico-finanziario" },
  { value: "altro",                       label: "Altro" },
];

const TEMPLATE_CRITERI = {
  base: [
    { nome: "Fattibilità tecnica del progetto",           categoria: "fattibilita_tecnica",         peso: 35, descrizione: "Valutazione della fattibilità tecnico-costruttiva dell'intervento proposto" },
    { nome: "Adeguatezza dei livelli di servizio",         categoria: "fabbisogni_ente",             peso: 30, descrizione: "Coerenza con i livelli di servizio richiesti dall'ente concedente" },
    { nome: "Equilibrio del Piano Economico-Finanziario", categoria: "piano_economico_finanziario", peso: 35, descrizione: "Sostenibilità finanziaria e equilibrio del PEF su tutta la durata della concessione" },
  ],
  infrastrutture: [
    { nome: "Fattibilità tecnica delle opere civili",     categoria: "fattibilita_tecnica",         peso: 40, descrizione: null },
    { nome: "Conformità ai fabbisogni dell'ente",         categoria: "fabbisogni_ente",             peso: 25, descrizione: null },
    { nome: "Sostenibilità del PEF",                      categoria: "piano_economico_finanziario", peso: 35, descrizione: null },
  ],
  servizi: [
    { nome: "Qualità tecnica del progetto di gestione",   categoria: "fattibilita_tecnica",         peso: 30, descrizione: null },
    { nome: "Rispondenza ai fabbisogni di servizio",      categoria: "fabbisogni_ente",             peso: 40, descrizione: null },
    { nome: "Equilibrio economico-finanziario",           categoria: "piano_economico_finanziario", peso: 30, descrizione: null },
  ],
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
      {_locate(`ppp_schema_stato_${stato}`) ?? stato}
    </span>
  );
}

function PesoBar({ totale }) {
  const ok = totale === 100;
  const color = ok ? "bg-green-500" : totale > 100 ? "bg-red-500" : "bg-yellow-400";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full transition-all ${color}`} style={{ width: `${Math.min(totale, 100)}%` }} />
      </div>
      <span className={`text-sm font-semibold ${ok ? "text-green-600" : totale > 100 ? "text-red-600" : "text-yellow-600"}`}>
        {totale}%
      </span>
    </div>
  );
}

function InlineDialog({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <p className="font-bold text-gray-800 dark:text-white">{title}</p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
            <BsX size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function SchemaValutazioneDetail() {
  const { procedimentoId, schemaId } = useParams();
  const navigate = useNavigate();
  const { _locate, addToast } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();

  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [note, setNote] = useState("");
  const [criteri, setCriteri] = useState([]);
  const [templateScelto, setTemplateScelto] = useState("");

  const [showApprova, setShowApprova] = useState(false);
  const [noteApprovazione, setNoteApprovazione] = useState("");
  const [approvando, setApprovando] = useState(false);

  const [showCriterioDialog, setShowCriterioDialog] = useState(false);
  const [criterioEditIndex, setCriterioEditIndex] = useState(null);
  const [criterioDraft, setCriterioDraft] = useState({ nome: "", categoria: "fattibilita_tecnica", peso: 10, descrizione: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.getSchemaValutazione(procedimentoId, schemaId, {
      onSuccess: (data) => {
        setSchema(data);
        setTitolo(data.titolo);
        setDescrizione(data.descrizione ?? "");
        setNote(data.note ?? "");
        setCriteri(data.criteri ?? []);
        setLoading(false);
      },
      onError: () => {
        addToast({ title: _locate("ppp_errore_generico"), type: "error" });
        setLoading(false);
      },
    });
  }, [procedimentoId, schemaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const totalePeso = criteri.reduce((sum, c) => sum + (parseInt(c.peso, 10) || 0), 0);
  const isApprovato = schema?.stato === "approvato";

  function handleApplicaTemplate(tpl) {
    if (!TEMPLATE_CRITERI[tpl]) return;
    setCriteri(TEMPLATE_CRITERI[tpl].map(c => ({ ...c })));
    setTemplateScelto(tpl);
  }

  function openCriterioDialog(index) {
    if (index !== null && index >= 0) {
      setCriterioDraft({ ...criteri[index] });
      setCriterioEditIndex(index);
    } else {
      setCriterioDraft({ nome: "", categoria: "fattibilita_tecnica", peso: 10, descrizione: "" });
      setCriterioEditIndex(null);
    }
    setShowCriterioDialog(true);
  }

  function saveCriterio() {
    if (!criterioDraft.nome?.trim()) return;
    const updated = [...criteri];
    if (criterioEditIndex !== null) {
      updated[criterioEditIndex] = { ...criterioDraft };
    } else {
      updated.push({ ...criterioDraft });
    }
    setCriteri(updated);
    setShowCriterioDialog(false);
  }

  function removeCriterio(index) {
    setCriteri(criteri.filter((_, i) => i !== index));
  }

  function handleSave() {
    setSaving(true);
    const criteriFiltrati = criteri
      .filter(c => c.nome?.trim())
      .map(c => ({ ...c, descrizione: c.descrizione || null }));
    api.aggiornaSchemaValutazione(procedimentoId, schemaId, { titolo, descrizione, criteri: criteriFiltrati, note }, {
      onSuccess: () => {
        setSaving(false);
        setEditMode(false);
        addToast({ title: _locate("ppp_schema_aggiornato"), type: "success" });
        load();
      },
      onError: () => {
        setSaving(false);
        addToast({ title: _locate("ppp_errore_generico"), type: "error" });
      },
    });
  }

  function handleApprova() {
    setApprovando(true);
    api.approvaSchemaValutazione(procedimentoId, schemaId, { noteApprovazione }, {
      onSuccess: () => {
        setApprovando(false);
        setShowApprova(false);
        addToast({ title: _locate("ppp_schema_approvato"), type: "success" });
        load();
      },
      onError: (err) => {
        setApprovando(false);
        const msg = err?.errors?.criteri?.[0] ?? err?.errors?.stato?.[0] ?? _locate("ppp_errore_generico");
        addToast({ title: msg, type: "error" });
      },
    });
  }

  if (loading) return <Spinner />;
  if (!schema) return <div className="p-8 text-gray-500">{_locate("ppp_schema_non_trovato")}</div>;

  const categoriaCounts = CATEGORIA_OPTIONS.reduce((acc, cat) => {
    acc[cat.value] = criteri.filter(c => c.categoria === cat.value).reduce((s, c) => s + (parseInt(c.peso, 10) || 0), 0);
    return acc;
  }, {});

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center gap-3">
        <SimpleButton
          text={<><BsArrowLeft className="inline mr-1" />{_locate("ppp_torna_lista")}</>}
          customFunc={() => navigate(`/ppp/${procedimentoId}`)}
        />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{schema.titolo}</h1>
            <StatoBadge stato={schema.stato} _locate={_locate} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {_locate("ppp_schema_fase08_label")} — art. 193, co. 5 D.lgs 36/2023
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {!isApprovato && !editMode && can('ppp.schema_valutazione.edit') && (
            <SimpleButton text={_locate("ppp_modifica")} customFunc={() => setEditMode(true)} />
          )}
          {editMode && (
            <>
              <SimpleButton text={_locate("ppp_form_annulla")} customFunc={() => { setEditMode(false); load(); }} />
              <LoadingButton isLoading={saving} customFunc={handleSave} text={_locate("ppp_salva")} />
            </>
          )}
          {!isApprovato && !editMode && totalePeso === 100 && criteri.length > 0 && can('ppp.schema_valutazione.approve') && (
            <SimpleButton
              text={_locate("ppp_schema_approva_btn")}
              customFunc={() => setShowApprova(true)}
            />
          )}
          {!editMode && criteri.length > 0 && (
            <SimpleButton
              text={_locate("ppp_schema_scarica_pdf")}
              customFunc={() => window.open(api.urlSchemaPdf(procedimentoId, schemaId), '_blank')}
            />
          )}
        </div>
      </div>

      {isApprovato && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <BsCheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={18} />
          <div className="text-sm text-green-800">
            <span className="font-medium">{_locate("ppp_schema_approvato_da")}: </span>
            {schema.approvatoDaNome} — {formatDate(schema.approvatoIl)}
            {schema.noteApprovazione && <p className="mt-1 text-green-700">{schema.noteApprovazione}</p>}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {_locate("ppp_schema_sezione_generale")}
        </h2>
        {editMode ? (
          <div className="space-y-3">
            <Input label={_locate("ppp_schema_titolo")} value={titolo} onChange={e => setTitolo(e.target.value)} />
            <TextArea label={_locate("ppp_schema_descrizione")} value={descrizione} onChange={e => setDescrizione(e.target.value)} rows={3} />
            <TextArea label={_locate("ppp_schema_note")} value={note} onChange={e => setNote(e.target.value)} rows={2} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {schema.descrizione && (
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">{_locate("ppp_schema_descrizione")}</span>
                <p className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{schema.descrizione}</p>
              </div>
            )}
            {schema.note && (
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">{_locate("ppp_schema_note")}</span>
                <p className="text-sm text-gray-900 dark:text-white mt-0.5">{schema.note}</p>
              </div>
            )}
            <div className="flex gap-6">
              {schema.propostaPromotoreTitolo && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">{_locate("ppp_schema_proposta_promotore")}</span>
                  <p className="text-sm text-gray-900 dark:text-white mt-0.5">{schema.propostaPromotoreTitolo}</p>
                </div>
              )}
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">{_locate("ppp_schema_creato_il")}</span>
                <p className="text-sm text-gray-900 dark:text-white mt-0.5">{formatDate(schema.createdAt)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {_locate("ppp_schema_criteri_titolo")}
          </h2>
          {editMode && (
            <div className="flex items-center gap-2">
              <Select
                value={templateScelto}
                onChange={e => handleApplicaTemplate(e.target.value)}
                options={[
                  { value: "", label: _locate("ppp_schema_template_placeholder") },
                  { value: "base", label: _locate("ppp_schema_template_base") },
                  { value: "infrastrutture", label: _locate("ppp_schema_template_infrastrutture") },
                  { value: "servizi", label: _locate("ppp_schema_template_servizi") },
                ]}
              />
              <SimpleButton
                text={<><BsPlusLg className="inline mr-1" />{_locate("ppp_schema_aggiungi_criterio")}</>}
                customFunc={() => openCriterioDialog(null)}
              />
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">{_locate("ppp_schema_totale_peso")}</span>
            {totalePeso === 100 ? (
              <span className="text-xs text-green-600 font-medium">{_locate("ppp_schema_pesi_ok")}</span>
            ) : (
              <span className="text-xs text-yellow-600 font-medium">
                {totalePeso > 100 ? _locate("ppp_schema_pesi_troppo") : _locate("ppp_schema_pesi_mancanti").replace("{n}", 100 - totalePeso)}
              </span>
            )}
          </div>
          <PesoBar totale={totalePeso} />
        </div>

        {criteri.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {CATEGORIA_OPTIONS.filter(cat => cat.value !== "altro").map(cat => (
              <div key={cat.value} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 truncate">{cat.label}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{categoriaCounts[cat.value]}%</p>
              </div>
            ))}
          </div>
        )}

        {criteri.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">{_locate("ppp_schema_criteri_vuoti")}</p>
        ) : (
          <div className="space-y-2">
            {criteri.map((criterio, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{criterio.nome}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex-shrink-0">
                      {CATEGORIA_OPTIONS.find(c => c.value === criterio.categoria)?.label ?? criterio.categoria}
                    </span>
                  </div>
                  {criterio.descrizione && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{criterio.descrizione}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-base font-bold text-gray-700 dark:text-gray-300 w-12 text-right">{criterio.peso}%</span>
                  {editMode && (
                    <>
                      <button onClick={() => openCriterioDialog(idx)} className="text-gray-400 hover:text-blue-600 p-1">
                        <BsPencil size={14} />
                      </button>
                      <button onClick={() => removeCriterio(idx)} className="text-gray-400 hover:text-red-500 p-1">
                        <BsTrash size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!editMode && totalePeso !== 100 && criteri.length > 0 && !isApprovato && (
          <p className="text-xs text-yellow-600 bg-yellow-50 px-3 py-2 rounded">
            {_locate("ppp_schema_avviso_pesi")}
          </p>
        )}
      </div>

      <InlineDialog
        open={showCriterioDialog}
        title={criterioEditIndex !== null ? _locate("ppp_schema_modifica_criterio") : _locate("ppp_schema_aggiungi_criterio")}
        onClose={() => setShowCriterioDialog(false)}
      >
        <div className="space-y-3">
          <Input
            label={_locate("ppp_schema_criterio_nome")}
            value={criterioDraft.nome}
            onChange={e => setCriterioDraft(d => ({ ...d, nome: e.target.value }))}
          />
          <Select
            label={_locate("ppp_schema_criterio_categoria")}
            value={criterioDraft.categoria}
            onChange={e => setCriterioDraft(d => ({ ...d, categoria: e.target.value }))}
            options={CATEGORIA_OPTIONS}
          />
          <Input
            label={_locate("ppp_schema_criterio_peso")}
            type="number"
            min={1}
            max={100}
            value={criterioDraft.peso}
            onChange={e => setCriterioDraft(d => ({ ...d, peso: parseInt(e.target.value, 10) || 0 }))}
          />
          <TextArea
            label={_locate("ppp_schema_criterio_descrizione")}
            value={criterioDraft.descrizione ?? ""}
            onChange={e => setCriterioDraft(d => ({ ...d, descrizione: e.target.value }))}
            rows={2}
          />
          <div className="flex justify-end gap-2 pt-2">
            <SimpleButton customFunc={() => setShowCriterioDialog(false)} text={_locate("ppp_form_annulla")} />
            <SimpleButton customFunc={saveCriterio} text={_locate("ppp_salva")} />
          </div>
        </div>
      </InlineDialog>

      <InlineDialog
        open={showApprova}
        title={_locate("ppp_schema_approva_titolo")}
        onClose={() => setShowApprova(false)}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">{_locate("ppp_schema_approva_avviso")}</p>
          <TextArea
            label={_locate("ppp_schema_note_approvazione")}
            value={noteApprovazione}
            onChange={e => setNoteApprovazione(e.target.value)}
            rows={3}
            placeholder={_locate("ppp_schema_note_approvazione_hint")}
          />
          <div className="flex justify-end gap-2 pt-2">
            <SimpleButton customFunc={() => setShowApprova(false)} text={_locate("ppp_form_annulla")} />
            <LoadingButton isLoading={approvando} customFunc={handleApprova} text={_locate("ppp_schema_approva_conferma")} />
          </div>
        </div>
      </InlineDialog>
    </div>
  );
}
